import { captureException } from "@sentry/browser";
import { formatDistanceToNowStrict, isValid, parseISO } from "date-fns";
import { format, toZonedTime } from "date-fns-tz";

import { LocalStorageKeys } from "globalConstants/localStorageKeys";
import i18n from "translations/i18n";

import getLocale from "./dateLocalesList";
import { isDateValid } from "./isDateValid";
import { getItem } from "./localStorage";
import {
  extractDateFormatFromUserPrefenceDatetimeFormat,
  extractTimeFormatFromUserPrefenceDatetimeFormat,
} from "./userPreferences";

/**
 * Time mask.
 * E.g. 12:00:00
 */
export const timeMask = "HH:mm:ss";

/**
 * Hours and minutes (24 hours format).
 * E.g. 23:00
 */
export const hoursAndMinutes = "HH:mm";

/**
 * Hours and minutes (12 hours format).
 * E.g. 11:00 PM
 */
export const twelveHoursFormatAndMinutes = "hh:mm a";

/**
 * Mask with year, month, day, for backend usage.
 * E.g. 2020-12-25
 */
export const yearMonthDay = "yyyy-MM-dd";

/**
 * Mask with month and Year.
 * E.g. 12/2020
 */
export const yearMonth = "MM/yyyy";

/**
 * Mask for brazilian dates.
 * E.g. 25/12/2020
 */
export const dayMonthYear = "dd/MM/yyyy";

/**
 * Mask for brazilian date and time.
 * E.g. 25/12/2020 12:00
 */
export const dayMonthYearTime = `${dayMonthYear} ${hoursAndMinutes}`;

/**
 * Mask with month, day and year
 * E.g. 05/21/20
 */
export const monthDayYear = "MM/dd/yyyy";

/**
 * Reports an invalid date value to Sentry, so we keep visibility over places sending values that
 * cannot be formatted, without breaking the UI for the user.
 *
 * Every occurrence is reported on purpose: deduplicating by value would silence the second place
 * sending the same value, and finding every place that needs a fix is the whole point.
 * @param functionName the name of the formatting function that received the value.
 * @param date the invalid value.
 */
const reportInvalidDate = (functionName: string, date: Date | string | null | undefined): void => {
  captureException(new Error(`${functionName} received an invalid date value: ${String(date)}`));
};

/**
 * Tries to parse a date with `parseISO` when possible. Will fallback to the default `Date`
 * constructor in case the value is not valid for parsing or the parsed date is invalid.
 * @param date the date.
 */
export const parseDate = (date: Date | string): Date => {
  if (date && typeof date === "string") {
    const parsedDate = parseISO(date);

    if (isValid(parsedDate)) {
      return parsedDate;
    }
  }

  return new Date(date);
};

/**
 * Safely formats a date with a mask.
 * @param date the date.
 * @param mask the mask.
 * @param fallback fallback return value if date is invalid.
 */
export const formatDateByLocale = (
  date: Date | string,
  mask: string,
  fallback = "",
  timeZoneOverride?: string,
): string => {
  if (!date) {
    return fallback;
  }

  const currentLanguage = i18n.language;
  const currentLocale = getLocale(currentLanguage);
  const userTimeZone = getItem(LocalStorageKeys.TIMEZONE);
  const timeZone = timeZoneOverride ?? userTimeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

  const parsedDate = parseDate(date);

  if (!isValid(parsedDate)) {
    reportInvalidDate("formatDateByLocale", date);

    return fallback;
  }

  const zonedTimeDate = toZonedTime(parsedDate, timeZone);

  const result = format(zonedTimeDate, mask, { locale: currentLocale, timeZone });

  return result;
};

export const formatDistanceToNowInWords = (date: Date | string): string => {
  if (!date) {
    return "";
  }

  const currentLanguage = i18n.language;
  const currentLocale = getLocale(currentLanguage);
  const userTimeZone = getItem(LocalStorageKeys.TIMEZONE);
  const timeZone = userTimeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

  const parsedDate = parseDate(date);

  if (!isValid(parsedDate)) {
    reportInvalidDate("formatDistanceToNowInWords", date);

    return "";
  }

  const zonedTimeDate = toZonedTime(parsedDate, timeZone);

  const result = formatDistanceToNowStrict(zonedTimeDate, { locale: currentLocale, addSuffix: true });

  return result;
};

/**
 * Formats to date accordingly to the user preference if exists.
 * @param date the date.
 * @param fallback - optional - the fallback text if param `date` is not provided.
 * @param shouldUseTimezone - optional - should use timezone.
 */
export const formatDate = (
  date?: string | null,
  fallback = "-",
  shouldUseTimezone = true,
  twoDigitYear = false,
): string => {
  if (!date) {
    return fallback;
  }

  const userDateTimeFormat = getItem(LocalStorageKeys.DATETIME_FORMAT);
  const userDateMask = userDateTimeFormat
    ? extractDateFormatFromUserPrefenceDatetimeFormat(userDateTimeFormat)
    : undefined;

  let mask = userDateMask ?? dayMonthYear;

  if (twoDigitYear) {
    mask = mask.replace("yyyy", "yy");
  }

  if (shouldUseTimezone) {
    const parsedDate = parseISO(date);

    if (!isValid(parsedDate)) {
      reportInvalidDate("formatDate", date);

      return fallback;
    }

    return formatDateByLocale(parsedDate, mask, fallback);
  }

  const dateWithoutHours = date.split("T")[0];

  const parsedDateWithoutHours = parseISO(dateWithoutHours);

  if (!isValid(parsedDateWithoutHours)) {
    reportInvalidDate("formatDate", date);

    return fallback;
  }

  return format(parsedDateWithoutHours, mask);
};

export const formatDateWithoutLocale = (date: Date, mask = yearMonth): string => {
  if (!date) {
    return "-";
  }

  if (!isValid(date)) {
    reportInvalidDate("formatDateWithoutLocale", date);

    return "-";
  }

  return format(date, mask);
};

/**
 * Formats to a date with time accordingly to the user preference if exists.
 * @param date the date.
 * @param mask the mask.
 */
export const formatDateWithTime = (
  date?: Date | string | null,
  options?: { withSeconds?: boolean; fallback?: string; mask?: string; twoDigitYear?: boolean; timeZone?: string },
): string => {
  const { withSeconds = false, fallback = "-", twoDigitYear, mask: optionMask, timeZone } = options ?? {};

  if (!date) {
    return fallback;
  }

  if (optionMask) {
    const result = formatDateByLocale(date, optionMask, fallback, timeZone);

    return result;
  }

  let mask = getItem(LocalStorageKeys.DATETIME_FORMAT) ?? dayMonthYearTime;

  if (withSeconds) {
    if (mask.includes("a")) {
      mask = mask.replace(" a", "");

      mask = `${mask}:ss a`;
    } else {
      mask = `${mask}:ss`;
    }
  }

  if (twoDigitYear) {
    mask = mask.replace("yyyy", "yy");
  }

  const result = formatDateByLocale(date, mask, fallback, timeZone);

  return result;
};

/**
 * Formats to time accordingly to the user preference if exists.
 * @param date the date.
 * @param mask the mask.
 */
export const formatTime = (date: Date | string, fallback = "-"): string => {
  if (!date) {
    return fallback;
  }

  const userDateTimeFormat = getItem(LocalStorageKeys.DATETIME_FORMAT);
  const userTimeMask = userDateTimeFormat
    ? extractTimeFormatFromUserPrefenceDatetimeFormat(userDateTimeFormat)
    : undefined;

  const mask = userTimeMask ?? timeMask;

  return formatDateByLocale(date, mask, fallback);
};

export const formatDateToBackEnd = (date?: Date | string | null): string | null => {
  if (!date) {
    return null;
  }

  if (!isDateValid(new Date(date))) {
    reportInvalidDate("formatDateToBackEnd", date);

    return null;
  }

  const parsedDate = parseDate(date);

  const result = format(parsedDate, yearMonthDay);

  return result;
};
